import { Link, useParams, useSearchParams } from 'react-router';

import type { SubmissionResponse } from '../../api';
import { AsyncSection, DataTable, PageHeader, Panel, type Column } from '../../components';
import { formatDate, formatDateTime } from '../../format';
import { PATHS } from '../../routes/paths';
import { useSubmissionHistory, useSubmissionStanding } from '../queries';
import { OffCyclePanel } from './OffCyclePanel';
import { SubmissionCyclePanel } from './SubmissionCyclePanel';
import {
  CHANNEL_LABELS,
  SOURCE_LABELS,
  SUBMISSION_COPY,
  SUBMISSION_STATUS_LABELS,
  currentCycleMonth,
  proofOf,
} from './submissionCycle';

/**
 * One contract's month, at an address of its own.
 *
 * It used to open underneath the calendar, on the same page, which meant the
 * URL never changed: the contract being worked on was not bookmarkable, not
 * reloadable and not pasteable to a colleague — and on a long list the analyst
 * scrolled between the line and its panels, which is how the wrong line gets
 * worked on. It is a route now, carrying the opaque contract uuid and nothing
 * else.
 *
 * The cycle rides along as `?ciclo=YYYY-MM` rather than a path segment,
 * because the month is a question about the contract ("where does April
 * stand?") and not part of what the contract is. It falls back to the current
 * month on a cold load, so an old link still lands somewhere true.
 */
export function SubmissionContractPage() {
  const { contratoId = '' } = useParams();
  const [searchParams] = useSearchParams();
  const cycleMonth = searchParams.get('ciclo') ?? currentCycleMonth();

  const standing = useSubmissionStanding(contratoId, cycleMonth);
  const history = useSubmissionHistory(contratoId);

  return (
    <div className="page">
      <AsyncSection
        state={standing}
        loadingLabel="Carregando o fechamento…"
        errorTitle="Não foi possível carregar o fechamento"
      >
        {(data) => (
          <>
            <PageHeader
              title={SUBMISSION_COPY.contractTitle}
              description={`${data.company_name} · ${data.insurer_name} — ${data.cycle_month}`}
              actions={
                <Link className="button button--ghost" to={PATHS.staffSubmissions}>
                  {SUBMISSION_COPY.backToCalendar}
                </Link>
              }
            />
            <SubmissionCyclePanel standing={data} />
            {/* Beside the month rather than inside it. The panel above
                answers "what still has to go out this month?"; this one
                answers "is anybody uninsured while they wait for it?", and
                on most contracts its answer is that the policy says
                waiting is what happens. */}
            <OffCyclePanel standing={data} />

            <Panel
              title={SUBMISSION_COPY.historyTitle}
              description={SUBMISSION_COPY.historyDescription}
            >
              <AsyncSection
                state={history}
                loadingLabel="Carregando o histórico…"
                errorTitle="Não foi possível carregar o histórico"
                isEmpty={(data) => data.items.length === 0}
                emptyTitle="Nenhum envio"
                emptyDescription={SUBMISSION_COPY.noHistory}
              >
                {(data) => (
                  <DataTable
                    id="submission-attempts"
                    caption="Tentativas de envio deste contrato"
                    columns={HISTORY_COLUMNS}
                    rows={data.items}
                    rowKey={(row) => row.id}
                    emptyTitle="Nenhum envio"
                  />
                )}
              </AsyncSection>
            </Panel>
          </>
        )}
      </AsyncSection>
    </div>
  );
}

/**
 * Every attempt, refused ones included.
 *
 * `source` is a column rather than a detail because the two kinds of row
 * evidence different things: a `cycle` row can name the file the insurer has,
 * and a `manual` one is somebody asserting that a month went out before this
 * system existed. Reading them as though they were the same record is the
 * mistake the column exists to prevent.
 */
const HISTORY_COLUMNS: readonly Column<SubmissionResponse>[] = [
  {
    key: 'cycle',
    header: 'Ciclo',
    render: (row) => row.cycle_month ?? <span className="table__muted">—</span>,
  },
  { key: 'source', header: 'Origem', render: (row) => SOURCE_LABELS[row.source] },
  {
    key: 'status',
    header: 'Situação',
    render: (row) => SUBMISSION_STATUS_LABELS[row.status],
  },
  {
    key: 'covers',
    header: 'Cobre até',
    render: (row) => formatDate(row.covers_through),
  },
  {
    key: 'channel',
    header: 'Por onde',
    render: (row) =>
      row.channel != null ? CHANNEL_LABELS[row.channel] : <span className="table__muted">—</span>,
  },
  {
    key: 'when',
    header: 'Quando',
    render: (row) =>
      row.submitted_at != null ? (
        formatDateTime(row.submitted_at)
      ) : row.failed_at != null ? (
        formatDateTime(row.failed_at)
      ) : (
        <span className="table__muted">—</span>
      ),
  },
  {
    key: 'proof',
    header: 'Comprovação',
    render: (row) => {
      if (row.failure_reason != null) {
        return <span className="table__muted">{row.failure_reason}</span>;
      }
      const proof = proofOf(row);
      return proof.length > 0 ? proof.join(' · ') : <span className="table__muted">—</span>;
    },
  },
];
